import { Camera, CarFront, CheckCircle2, ClipboardCheck, Eye, FileSignature } from 'lucide-react';
import type { ReactNode } from 'react';
import type { PreArrivalProgress, PreRegistrationStatus } from '@cloudsign/shared';
import { StatusPill } from '../../components/phase2';
import type { StatusTone } from '../../components/phase2';
import { t } from '../../lib/i18n';

const TONE: Record<PreRegistrationStatus, StatusTone> = {
  pending_approval: 'warn',
  expected: 'info',
  arrived: 'good',
  cancelled: 'neutral',
  no_show: 'bad',
  rejected: 'bad',
};

export function StatusLabel({ status }: { status: PreRegistrationStatus }) {
  return <StatusPill tone={TONE[status]}>{t(`prereg.status.${status}`)}</StatusPill>;
}

export interface ProgressItem {
  key: string;
  icon: ReactNode;
  label: string;
  done: boolean;
  /** Extra words, e.g. "1 of 2" or the car registration. */
  detail?: string;
}

/** What the visitor has done on the pre-arrival page, as a list of items (used by the icons and the drawer). */
export function progressItems(p: PreArrivalProgress): ProgressItem[] {
  const cls = 'h-4 w-4';
  return [
    { key: 'opened', icon: <Eye aria-hidden="true" className={cls} />, label: t('prereg.progress.opened'), done: p.opened },
    { key: 'confirmed', icon: <CheckCircle2 aria-hidden="true" className={cls} />, label: t('prereg.progress.confirmed'), done: p.confirmed },
    { key: 'answers', icon: <ClipboardCheck aria-hidden="true" className={cls} />, label: t('prereg.progress.answers'), done: p.answersDone },
    {
      key: 'notices',
      icon: <FileSignature aria-hidden="true" className={cls} />,
      label: t('prereg.progress.notices'),
      done: p.noticesRequired > 0 && p.noticesAccepted.length >= p.noticesRequired,
      detail: p.noticesRequired > 0 ? t('prereg.progress.noticesCount', { done: p.noticesAccepted.length, total: p.noticesRequired }) : undefined,
    },
    { key: 'photo', icon: <Camera aria-hidden="true" className={cls} />, label: t('prereg.progress.photo'), done: p.photoDone },
    {
      key: 'car',
      icon: <CarFront aria-hidden="true" className={cls} />,
      label: t('prereg.progress.car'),
      done: p.carRegistration !== null && p.carRegistration !== '',
      detail: p.carRegistration ?? undefined,
    },
  ];
}

/** A row of small icons; every one has text for screen readers and a tooltip, and "done" is not shown by colour alone (solid vs faded). */
export function ProgressIcons({ progress }: { progress: PreArrivalProgress }) {
  return (
    <ul className="flex gap-1.5" aria-label={t('prereg.progress.label')}>
      {progressItems(progress).map((i) => (
        <li
          key={i.key}
          title={`${i.label}: ${i.done ? t('prereg.progress.done') : t('prereg.progress.notYet')}`}
          className={i.done ? 'text-green-700' : 'text-slate-400'}
        >
          {i.icon}
          <span className="sr-only">
            {i.label}: {i.done ? t('prereg.progress.done') : t('prereg.progress.notYet')}
          </span>
        </li>
      ))}
    </ul>
  );
}
